// Radha — hooks for the global desk features (074-078):
// withholding tax, settlement outlook, limitation watch, debtor DNA,
// group exposure. One file because they share the read-only RPC pattern.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

// ---- G1: withholding (074) -------------------------------------------------
export interface WhtCandidate {
  invoice_id: string; invoice_number: string; customer_name: string;
  total_amount: string; taxable_amount: string; amount_paid: string;
  gap: string; gap_pct_of_taxable: string; gap_pct_of_total: string;
  matched_section: string; matched_rate: string; matched_base: string; deviation_pp: string;
}
export interface WhtCredit {
  id: string; invoice_id: string | null; customer_name: string;
  amount: string; currency: string; rule_section: string | null; rate_pct: string | null;
  status: "expected" | "certificate_received" | "matched_26as" | "mismatch" | "written_off";
  expected_quarter: string | null; certificate_ref: string | null;
  resolved_date: string | null; notes: string | null; created_at: string;
}
export interface WithholdingRule {
  country: string; section: string; label: string; rate_pct: string; base: string;
  threshold: string | null; is_statutory: boolean; statute_ref: string | null; notes: string | null;
}

export function useWhtCandidates(country?: string) {
  return useQuery({
    queryKey: ["wht-candidates", country ?? ""],
    queryFn: async (): Promise<WhtCandidate[]> => {
      const { data, error } = await supabase.rpc("detect_wht_candidates", country ? { p_country: country } : {});
      if (error) throw error;
      return (data as WhtCandidate[]) ?? [];
    },
    staleTime: 30_000,
  });
}
export function useWhtCredits() {
  return useQuery({
    queryKey: ["wht-credits"],
    queryFn: async (): Promise<WhtCredit[]> => {
      const { data, error } = await supabase
        .from("wht_credits")
        .select("id, invoice_id, customer_name, amount, currency, rule_section, rate_pct, status, expected_quarter, certificate_ref, resolved_date, notes, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 20_000,
  });
}
export function useWithholdingRules(country: string | null | undefined) {
  return useQuery({
    queryKey: ["wht-rules", country ?? ""],
    enabled: !!country,
    queryFn: async (): Promise<WithholdingRule[]> => {
      const { data, error } = await supabase
        .from("withholding_rules")
        .select("country, section, label, rate_pct, base, threshold, is_statutory, statute_ref, notes")
        .eq("country", country)
        .order("rate_pct", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 600_000,
  });
}
/** Recognise a detected candidate as a WHT credit (certificate expected). */
export function useRecogniseWht() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: WhtCandidate) => {
      const { error } = await supabase.from("wht_credits").insert({
        invoice_id: c.invoice_id, customer_name: c.customer_name,
        amount: Number(c.gap), rule_section: c.matched_section,
        rate_pct: Number(c.matched_rate),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wht-candidates"] });
      qc.invalidateQueries({ queryKey: ["wht-credits"] });
    },
  });
}
/** Advance a credit through its certificate lifecycle. */
export function useAdvanceWht() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, certificate_ref }: { id: string; status: WhtCredit["status"]; certificate_ref?: string }) => {
      const terminal = status === "matched_26as" || status === "written_off";
      const { error } = await supabase.from("wht_credits").update({
        status, certificate_ref: certificate_ref ?? null,
        resolved_date: terminal ? new Date().toISOString().slice(0, 10) : null,
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wht-credits"] }),
  });
}

// ---- G2: settlement outlook (075) -----------------------------------------
export interface SettlementRow {
  promise_id: string; customer_name: string; promised_date: string;
  promised_amount: string; rail: string; rail_label: string;
  expected_landing: string; slip_days: number;
}
export function useSettlementOutlook(country?: string) {
  return useQuery({
    queryKey: ["settlement-outlook", country ?? ""],
    queryFn: async (): Promise<SettlementRow[]> => {
      const { data, error } = await supabase.rpc("settlement_outlook", country ? { p_country: country } : {});
      if (error) throw error;
      return (data as SettlementRow[]) ?? [];
    },
    staleTime: 60_000,
  });
}

// ---- G3: limitation watch (076) -------------------------------------------
export interface LimitationRow {
  invoice_id: string; invoice_number: string; customer_name: string;
  outstanding: string; currency: string;
  anchor_date: string; anchor_source: string;
  expiry_date: string; days_left: number;
  rule_years: number; statute_ref: string | null; verified: boolean;
}
export function useLimitationWatchlist(country?: string) {
  return useQuery({
    queryKey: ["limitation-watchlist", country ?? ""],
    queryFn: async (): Promise<LimitationRow[]> => {
      const { data, error } = await supabase.rpc("limitation_watchlist", country ? { p_country: country } : {});
      if (error) throw error;
      return (data as LimitationRow[]) ?? [];
    },
    staleTime: 300_000,
  });
}

// ---- G4: debtor DNA (077) --------------------------------------------------
export interface DebtorDna {
  customer_name: string; paid_invoices: number;
  avg_lag_days: string | null; lag_stddev: string | null; typical_pay_dom: number | null;
  deduction_count: number; deduction_rate_pct: string | null; wht_count: number;
  open_promises: number; broken_promises: number; outstanding: string;
}
export function useDebtorDna() {
  return useQuery({
    queryKey: ["debtor-dna"],
    queryFn: async (): Promise<DebtorDna[]> => {
      const { data, error } = await supabase.rpc("debtor_dna");
      if (error) throw error;
      return (data as DebtorDna[]) ?? [];
    },
    staleTime: 120_000,
  });
}

// ---- G5: group exposure (078) ----------------------------------------------
export interface GroupExposureRow {
  customer_name: string; entity_count: number; entities: string;
  gross_receivable: string; open_credit_notes: string; open_deductions: string;
  net_position: string; currencies: string;
}
export function useGroupExposure() {
  return useQuery({
    queryKey: ["group-exposure"],
    queryFn: async (): Promise<GroupExposureRow[]> => {
      const { data, error } = await supabase.rpc("group_exposure");
      if (error) throw error;
      return (data as GroupExposureRow[]) ?? [];
    },
    staleTime: 120_000,
  });
}

// ---- CFO-1: 13-week cash forecast (080) ------------------------------------
export interface ForecastWeek {
  week_index: number; week_start: string | null;
  expected: string; low: string | null; high: string | null;
  contractual: string | null; promise_backed: string | null; invoice_count: number;
}
export function useCashForecast(country?: string) {
  return useQuery({
    queryKey: ["cash-forecast", country ?? ""],
    queryFn: async (): Promise<ForecastWeek[]> => {
      const { data, error } = await supabase.rpc("cash_forecast_13w", country ? { p_country: country } : {});
      if (error) throw error;
      return (data as ForecastWeek[]) ?? [];
    },
    staleTime: 300_000,
  });
}

export function deskErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
